// meeting-guides-grid.ts — Dérivations pures de la vue « Guides » de
// #meetings-records : matrice réunions × guides.
//
// Extrait du rendu pour être testable sans DOM : AUCUN accès à window/document,
// aucun appel à `t()`. Le module trie et projette, le module de page met en mots.

import type { GuideStatus } from './meeting-guides.js';

/** Un guide non sélectionné pour le comité de la réunion : pas de colonne
 *  applicable, à distinguer d'un guide simplement pas encore traité. */
export type GridCell = GuideStatus | 'absent';

export interface GridMeeting {
  id: number;
  label: string;
  program_id: number | null;
  project_code: string | null;
  // Libellés : affichés au survol des codes, pas dans la cellule.
  project_name: string | null;
  committee_code: string | null;
  committee_name: string | null;
  meeting_date: string | null;
  meeting_time: string | null;
}

/** État par définition de guide, pour une réunion. Clés en chaînes : c'est
 *  ce que rend JSON, on n'ajoute pas une conversion pour le plaisir. */
export type StatusByDefinition = Record<string, GuideStatus>;

/** Colonne de guide sur laquelle on peut trier : `guide:<definition_id>`. */
export type GridSortKey =
  'id' | 'label' | 'project_code' | 'committee_code' | 'meeting_date' | `guide:${number}`;

/**
 * Ordre de tri des états : ce qui reste à faire d'abord.
 *
 * En croissant, on veut voir remonter les guides à traiter — c'est ce que
 * l'animateur cherche en cliquant sur une colonne. « Absent » ferme la marche :
 * il ne demande aucune action.
 */
const STATUS_RANK: Record<GridCell, number> = {
  pending: 0,
  skipped: 1,
  done: 2,
  absent: 3,
};

export function cellFor(statuses: StatusByDefinition | undefined, definitionId: number): GridCell {
  return statuses?.[String(definitionId)] ?? 'absent';
}

/** Identifiant de définition porté par une clé de colonne, ou null. */
export function definitionIdOf(key: GridSortKey): number | null {
  if (!key.startsWith('guide:')) return null;
  const id = Number(key.slice('guide:'.length));
  return Number.isFinite(id) ? id : null;
}

/**
 * Tri stable de la matrice. `matrix` est indexée par identifiant de réunion (en
 * chaîne), comme la réponse de l'API.
 *
 * Les valeurs manquantes (pas de projet, pas de date) descendent en fin de tri
 * croissant plutôt que de se mélanger aux valeurs basses : une réunion sans
 * date n'est pas « la plus ancienne ».
 */
export function sortGrid(
  meetings: readonly GridMeeting[],
  matrix: Record<string, StatusByDefinition>,
  key: GridSortKey,
  dir: 'asc' | 'desc',
): GridMeeting[] {
  const definitionId = definitionIdOf(key);
  const value = (m: GridMeeting): string | number => {
    if (definitionId !== null) return STATUS_RANK[cellFor(matrix[String(m.id)], definitionId)];
    switch (key) {
      case 'label':
        return m.label.toLowerCase();
      case 'project_code':
        return (m.project_code || '￿').toLowerCase();
      case 'committee_code':
        return (m.committee_code || '￿').toLowerCase();
      case 'meeting_date':
        return m.meeting_date || '￿';
      default:
        return m.id;
    }
  };
  return [...meetings].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    // Départage par identifiant : deux réunions au même état ne doivent pas
    // changer de place d'un rendu à l'autre.
    return a.id - b.id;
  });
}
